import { apiClient } from '@indipia/shared/api/client';
import { API_PRX } from '@indipia/shared/api/endpoints';
import { useAuthStore } from '@indipia/shared/stores/authStore';
import type { ApiResponse } from '@indipia/shared-types';
import type { ShowcaseContent, ShowcaseSite, ImageRef, Page } from '../model/types';

const B = `${API_PRX.PORTFOLIO}/showcase`;
/** AI 호출은 오래 걸린다(사진 편집 10~20초, 문체 가이드 첫 생성 1분 안팎) */
const AI_TIMEOUT = 240000;

export interface UsageLite { total: number; input: number; output: number; thinking: number; images: number; searches: number }
export interface UsageEntry { aiType: string; label: string; total: number; images: number; reqKey: string | null; at: string }
export interface UsageSummary {
  input: number; output: number; thinking: number; total: number; imageInput: number; images: number; searches: number; calls: number;
  byType: Record<string, { label: string; total: number; calls: number }>;
  recent: UsageEntry[];
}
export interface PhotoPick { photoId: number; seq: number; cover: boolean; repGenerator?: string | null }
export interface ShowcaseProjectBody {
  title: string; siteId: number | null; templateCode: string | null; themeCode: string | null; bundle: boolean;
  content: ShowcaseContent; photos?: PhotoPick[];
  /** 쓴 사진의 현장별 사진 수 — null 이면 서버의 연결을 그대로 둔다 */
  sites?: SiteUse[] | null;
}
export interface SiteUse { siteId: number; photoCount: number }
export interface ShowcaseProject {
  projectId: number; title: string; siteId: number | null; templateCode: string | null; themeCode: string | null; bundle: boolean;
  content: ShowcaseContent; publicYn: string; publicUrl: string | null; photos: PhotoPick[]; updatedAt: string;
}
export interface ShowcaseListItem {
  projectId: number; title: string; pageCount: number; cover: Page | null; publicYn: string; publicUrl: string | null;
  viewCount: number; updatedAt: string;
  /** 이 시공사례에 쓴 사진의 현장 — 모음집은 여러 개 */
  sites: SiteUse[];
}
export interface SpaceGuess { photoId: number; spaceTag: string; spaceName: string; confidence: number | null }

const data = <T,>(p: Promise<{ data: ApiResponse<T> }>) => p.then((r) => r.data.data as T);

/** 인디포트폴리오 AI 시공사례 API — /api/portfolio/showcase/... */
export const showcaseApi = {
  /** siteId — 그 현장 사진이 들어간 시공사례만(모음집 포함) */
  list: (siteId?: number | null) => data<ShowcaseListItem[]>(apiClient.get(`${B}/projects`, { params: siteId ? { siteId } : {} })),
  get: (id: number) => data<ShowcaseProject>(apiClient.get(`${B}/projects/${id}`)),
  create: (body: ShowcaseProjectBody) => data<number>(apiClient.post(`${B}/projects`, body, { timeout: 30000 })),
  save: (id: number, body: ShowcaseProjectBody, keepalive = false) =>
    keepalive ? saveKeepalive(id, body) : data<void>(apiClient.put(`${B}/projects/${id}`, body, { timeout: 60000 })),
  setPublic: (id: number, on: boolean) => data<ShowcaseProject>(apiClient.post(`${B}/projects/${id}/visibility`, { public: on }, { timeout: 60000 })),
  remove: (id: number) => data<void>(apiClient.delete(`${B}/projects/${id}`)),

  /** mine=true 면 로그인 회원이 담당자·관리자인 현장만, 기본은 우리 회사 모든 현장 */
  photos: (siteId?: number | null, mine = false) =>
    data<ShowcaseSite[]>(apiClient.get(`${B}/photos`, { params: { ...(siteId ? { siteId } : {}), ...(mine ? { mine: true } : {}) }, timeout: 30000 })),
  classify: (photos: ImageRef[]) => data<SpaceGuess[]>(apiClient.post(`${B}/photos/space/classify`, { photos }, { timeout: AI_TIMEOUT })),
  updateSpace: (photoId: number, spaceTag: string) => data<SpaceGuess>(apiClient.put(`${B}/photos/${photoId}/space`, { spaceTag })),

  generate: (body: AiTextBody, signal?: AbortSignal) =>
    data<{ text: string; usage: UsageLite }>(apiClient.post(`${B}/ai/generate`, body, { timeout: AI_TIMEOUT, signal })),
  imageEdit: (body: ImageEditBody, signal?: AbortSignal) =>
    data<{ aiImgId: number; url: string; usage: UsageLite }>(apiClient.post(`${B}/ai/image-edit`, body, { timeout: AI_TIMEOUT, signal })),
  confirmImage: (aiImgId: number, applied: boolean) => data<void>(apiClient.post(`${B}/ai/images/${aiImgId}/confirm`, { applied })),
  rank: (body: { siteId: number | null; spaceTag: string; photos: ImageRef[] }) =>
    data<{ order: number[]; reason: string; cached: boolean }>(apiClient.post(`${B}/ai/rank-photos`, body, { timeout: AI_TIMEOUT })),
  styleGuide: (p: { style: string; label?: string; tone?: string }, signal?: AbortSignal) =>
    data<{ styleCode: string; guide: string; cached: boolean }>(apiClient.get(`${B}/ai/style-guide`, { params: p, timeout: AI_TIMEOUT, signal })),
  /** 블로그용 사진 한 장(PNG·JPG) → 공개 주소. kind=page 는 본문 사진 칸, photo 는 시공 사진 원본. page 1번을 올리면 예전 블로그용 사진을 지운다 */
  blogImage: (id: number, kind: 'page' | 'photo', no: number, stamp: string, img: Blob) =>
    data<string>(apiClient.post(`${B}/projects/${id}/blog-images`, img, { params: { kind, no, stamp }, headers: { 'Content-Type': img.type === 'image/jpeg' ? 'image/jpeg' : 'image/png' }, timeout: 120000 })),
  usageToday: () => data<UsageSummary>(apiClient.get(`${B}/usage/today`)),
};

export interface AiTextBody {
  projectId: number | null; aiType: string; prompt: string; images?: ImageRef[]; json?: boolean; temperature?: number; reqKey?: string;
}
export interface ImageEditBody {
  projectId: number | null; source: ImageRef; originalPhotoId?: number | null; marked?: string; prompt: string;
  ref?: ImageRef | null; changeType: 'CHAT' | 'MARKUP' | 'MATCH'; points?: unknown; reqKey?: string;
}

/* ── fetch 로 직접 부르는 것(스트리밍·페이지를 떠날 때 저장) — apiClient 와 같은 헤더를 붙인다 ── */
const API_BASE = ((import.meta as unknown as { env?: Record<string, string> }).env?.VITE_API_BASE) || '';
function authHeaders(): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json', 'X-Client-Type': 'WEB' };
  const { accessToken, user } = useAuthStore.getState();
  if (accessToken) h.Authorization = `Bearer ${accessToken}`;
  if ((import.meta as unknown as { env?: Record<string, string> }).env?.VITE_DEV_BYPASS_AUTH === 'true' && user) {
    h['X-Comp-Id'] = String(user.compId);
    h['X-User-Id'] = String(user.memberId);
    h['X-Roles'] = user.masterYn === 'Y' ? 'OWNER' : 'STAFF';
  }
  return h;
}
function saveKeepalive(id: number, body: ShowcaseProjectBody): Promise<void> {
  const json = JSON.stringify(body);
  return fetch(`${API_BASE}/api${B}/projects/${id}`, { method: 'PUT', headers: authHeaders(), body: json, keepalive: json.length < 60000, credentials: 'include' })
    .then(() => undefined);
}

/** AI 글 실시간 생성 — 줄마다 {"response":"조각"}, 끝에 {"done":true,"usage":{…}}, 오류 {"error":"…"} */
export async function streamText(body: AiTextBody, onText: (full: string) => void, signal?: AbortSignal): Promise<{ text: string; usage: UsageLite | null }> {
  let res = await fetch(`${API_BASE}/api${B}/ai/stream`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body), signal, credentials: 'include' });
  if (res.status === 401) {
    // 토큰이 막 만료됐으면 apiClient 가 갱신하게 한 번 가볍게 부른 뒤 다시
    await showcaseApi.usageToday().catch(() => undefined);
    res = await fetch(`${API_BASE}/api${B}/ai/stream`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body), signal, credentials: 'include' });
  }
  if (!res.ok || !res.body) {
    const j = await res.json().catch(() => ({} as { message?: string }));
    throw new Error((j as { message?: string }).message || `HTTP ${res.status}`);
  }
  const reader = res.body.getReader(), dec = new TextDecoder();
  const typer = smoothTyper(onText);
  let buf = '', out = '', usage: UsageLite | null = null;
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let nl: number;
      while ((nl = buf.indexOf('\n')) >= 0) {
        const line = buf.slice(0, nl).trim();
        buf = buf.slice(nl + 1);
        if (!line) continue;
        let j: { response?: string; error?: string; done?: boolean; usage?: UsageLite };
        try { j = JSON.parse(line); } catch { continue; }
        if (j.error) throw new Error(j.error);
        if (j.response) { out += j.response; typer.push(out); }
        if (j.done) usage = j.usage || null;
      }
    }
    await typer.finish(signal);
  } finally {
    typer.stop();
  }
  return { text: out, usage };
}

/* 글이 자연스럽게 이어지게 — AI는 글을 문장 덩어리로 띄엄띄엄 보내서(앞에 생각하는 시간도 있다) 그대로 그리면
   한참 멈췄다가 확 나온다. 받은 글은 쌓아 두고 화면에는 한 글자씩 일정하게 풀어 그린다.
   밀린 글이 많을수록 빨리(약 0.8초 안에 따라잡게), 적으면 사람이 치는 속도로. 움직임 줄이기 설정이면 바로 그린다 */
function smoothTyper(onText: (shown: string) => void) {
  const instant = typeof window === 'undefined' || !window.requestAnimationFrame
    || !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  let target = '', shown = 0, raf = 0, last = 0, carry = 0;
  let resolveDone: (() => void) | null = null;
  const MIN_CPS = 40; // 밀린 글이 적을 때 초당 글자 수
  const CATCH_UP_S = 0.8; // 밀린 글을 이 시간 안에 따라잡는다
  const tick = (now: number) => {
    const dt = last ? Math.min(0.1, (now - last) / 1000) : 1 / 60;
    last = now;
    const backlog = target.length - shown;
    carry += Math.max(MIN_CPS, backlog / CATCH_UP_S) * dt;
    const n = Math.min(backlog, Math.floor(carry));
    if (n > 0) { carry -= n; shown += n; onText(target.slice(0, shown)); }
    if (shown < target.length) { raf = requestAnimationFrame(tick); return; }
    raf = 0; last = 0; carry = 0;
    resolveDone?.();
  };
  return {
    push(full: string) {
      target = full;
      if (instant) { shown = full.length; onText(full); return; }
      if (!raf) raf = requestAnimationFrame(tick);
    },
    /** 받은 글을 다 그릴 때까지 기다린다(■ 중지면 바로) */
    finish(signal?: AbortSignal) {
      if (instant || document.hidden || shown >= target.length || signal?.aborted) {
        if (shown < target.length) { shown = target.length; onText(target); }
        return Promise.resolve();
      }
      return new Promise<void>((res) => { resolveDone = res; signal?.addEventListener('abort', () => res(), { once: true }); });
    },
    stop() { if (raf) cancelAnimationFrame(raf); raf = 0; },
  };
}

export function errorMessage(e: unknown): string {
  const ax = e as { response?: { data?: { message?: string } }; message?: string };
  return ax?.response?.data?.message || ax?.message || '알 수 없는 오류';
}
export const isAbort = (e: unknown) => {
  const x = e as { name?: string; message?: string; code?: string };
  return !!x && (x.name === 'AbortError' || x.name === 'CanceledError' || x.code === 'ERR_CANCELED' || /abort|cancel/i.test(x.message || ''));
};
